import React, { useEffect, useRef, useState } from 'react';
import { tokenStyleFor } from '../../lib/categoryStyles';

// Renders answer_text as selectable word tokens.
// Interaction:
//   - Tap / click a word           -> applies the active category (primary)
//   - Press + drag across words    -> applies to the whole range (touch & mouse)
//   - Keyboard: 1-6 set category, 0/Backspace clear, arrows move, Enter applies active
// Drag works on touchscreens by releasing the implicit pointer capture and
// resolving the word under the finger with document.elementFromPoint during
// pointermove (pointerenter does not fire mid-touch-drag). The token surface uses
// touch-action:none so a drag highlights instead of scrolling the page.
// Tokens carry char offsets into answer_text; we never concatenate segment_text.
export const AnnotatableText = ({
  tokens,
  annotations,
  legendMap,
  orderedCodes,
  activeCategory, // category_code | 'ERASE'
  onApplyRange,
  onRangeApplied, // (size) => void — fired after a drag/tap applies, for the coach hint
  disabled = false,
  reveal = false,
  goldByToken = null, // map tokenIndex -> category_code (only used when reveal)
  testIdPrefix = 'token',
}) => {
  const selecting = useRef(false);
  const anchor = useRef(null);
  const tokenRefs = useRef([]);
  const selRange = useRef(null);
  const rafRef = useRef(0);
  const [preview, setPreview] = useState(null); // {a,b} live drag range
  const [popped, setPopped] = useState(null); // {lo,hi} tokens to briefly animate

  const buzz = (pattern) => { try { navigator.vibrate && navigator.vibrate(pattern); } catch (_) { /* noop */ } };

  useEffect(() => {
    const up = () => {
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
      if (selecting.current && selRange.current) {
        const { a, b } = selRange.current;
        const lo = Math.min(a, b);
        const hi = Math.max(a, b);
        onApplyRange(lo, hi, activeCategory);
        const size = hi - lo + 1;
        buzz(size > 1 ? [12, 28, 18] : 14); // longer, satisfying pulse for a drag
        setPopped({ lo, hi });
        setTimeout(() => setPopped(null), 320);
        if (onRangeApplied) onRangeApplied(size);
      }
      selecting.current = false;
      selRange.current = null;
      setPreview(null);
    };
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [activeCategory, onApplyRange, onRangeApplied]);

  const handleDown = (e, idx) => {
    if (disabled) return;
    e.preventDefault();
    buzz(8); // light tick on press
    // Release implicit pointer capture so a touch-drag dispatches over other words.
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    selecting.current = true;
    anchor.current = idx;
    selRange.current = { a: idx, b: idx };
    setPreview({ a: idx, b: idx });
  };

  const handleMove = (e) => {
    if (disabled || !selecting.current) return;
    const { clientX, clientY } = e;
    if (rafRef.current) return; // throttle to one update per frame
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const el = document.elementFromPoint(clientX, clientY);
      const tokenEl = el && el.closest('[data-tindex]');
      if (tokenEl) {
        const idx = Number(tokenEl.dataset.tindex);
        if (!Number.isNaN(idx)) {
          selRange.current = { a: anchor.current, b: idx };
          setPreview({ a: anchor.current, b: idx });
        }
      }
    });
  };

  const handleKeyDown = (e, token, idx) => {
    if (disabled) return;
    const refs = tokenRefs.current.filter(Boolean);
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = refs.find((r) => Number(r.dataset.tindex) > idx);
      if (next) next.focus();
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prevs = refs.filter((r) => Number(r.dataset.tindex) < idx);
      if (prevs.length) prevs[prevs.length - 1].focus();
      return;
    }
    if (!token.scored) return;
    if (e.key >= '1' && e.key <= String(orderedCodes.length)) {
      e.preventDefault();
      onApplyRange(idx, idx, orderedCodes[Number(e.key) - 1]);
    } else if (e.key === '0' || e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      onApplyRange(idx, idx, 'ERASE');
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onApplyRange(idx, idx, activeCategory);
    }
  };

  const lo = preview ? Math.min(preview.a, preview.b) : -1;
  const hi = preview ? Math.max(preview.a, preview.b) : -2;

  return (
    <div
      className="text-lg leading-loose text-slate-800 select-none"
      style={{ touchAction: disabled ? 'auto' : 'none' }}
      onPointerMove={disabled ? undefined : handleMove}
      data-testid={`${testIdPrefix}-surface`}
    >
      {tokens.map((token, i) => {
        const code = reveal
          ? (token.scored ? (goldByToken ? goldByToken[token.index] : null) : null)
          : annotations[token.index];
        const cat = code ? legendMap[code] : null;
        const style = token.scored ? tokenStyleFor(cat) : {};
        const interactive = token.scored && !disabled;
        const inPreview = token.scored && token.index >= lo && token.index <= hi;
        const isPopping = popped && token.scored && token.index >= popped.lo && token.index <= popped.hi;
        return (
          <React.Fragment key={token.index}>
            <span
              ref={(el) => (tokenRefs.current[i] = el)}
              data-tindex={token.index}
              data-testid={`${testIdPrefix}-${token.index}`}
              role={interactive ? 'button' : undefined}
              aria-pressed={interactive ? Boolean(code) : undefined}
              aria-label={
                interactive
                  ? `${token.text}${cat ? `, marked ${cat.display_name}` : ', not marked'}`
                  : undefined
              }
              tabIndex={interactive ? 0 : -1}
              onPointerDown={interactive ? (e) => handleDown(e, token.index) : undefined}
              onKeyDown={interactive ? (e) => handleKeyDown(e, token, token.index) : undefined}
              className={
                (interactive
                  ? 'cursor-pointer rounded px-0.5 transition-colors duration-150 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500'
                  : 'px-0.5')
                + (inPreview ? ' ring-2 ring-blue-500 bg-blue-100' : '')
                + (isPopping ? ' qh-pop inline-block' : '')
              }
              style={style}
              title={cat ? cat.display_name : undefined}
            >
              {token.text}
              {cat && (
                <sup className="ml-0.5 text-[10px] font-bold align-super">{cat.short_label}</sup>
              )}
            </span>
            {' '}
          </React.Fragment>
        );
      })}
    </div>
  );
};
